import { useForm, useWatch } from 'react-hook-form';
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  attachFiles,
  catchErrorMessage,
  getExactFileName,
  getFileDetails,
  handlePageScrollPosition,
} from 'utility/helpers';
import KnowledgeAPI, { FeedFilter } from 'api/KnowledgeAPI';
import { toast } from 'react-toastify';
import { DRAFTS, HOME, VIEW_DRAFT } from 'constants/routes';
import useUserData, { useUserArticlesQuery } from 'hooks/useUserData';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArticleFieldsType,
  ArticleRelatedRecordType,
  ArticleSourceType,
  ArticleType,
  AttachmentType,
  CustomFieldValueType,
  useArticlesAndPlaylistsPaginationQuery,
} from 'pages/knowledge/hooks/useArticlesQuery';
import useArticleDetailsQuery, {
  updateArticleCommentsCache,
  updateArticleDetailsCache,
} from 'pages/article/hooks/useArticleDetailsQuery';
import {
  ProjectsPaginationResponseType,
  ProjectRecommendationsType,
} from 'pages/projects/hooks/useProjectsQuery';
import { updateDraftsCache } from 'pages/drafts/hooks/useDraftsQuery';
import axios, { AxiosResponse } from 'axios';
import _ from 'lodash';
import {
  AUDIO_TYPES,
  DOCUMENT_MIME_WITH_EXTENSIONS_TYPES,
  DOCUMENT_TYPES,
  IMAGE_TYPES,
  VIDEO_TYPES,
} from 'components/utils/constants';
import { updateProjectRecommendationsQueryCache } from 'pages/project/hooks/useProjectDetailsQuery';
import { updateStakeholderArticlesCache } from 'pages/stakeholder/hooks/useStakeholderDetailsQuery';
import { useSearchItemQuery } from 'hooks/useSearchQuery';
import useBookmarksQuery from 'pages/bookmarks/hooks/useBookmarksQuery';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { ConfigEntryType, ConfigFieldEntryType, ConfigType } from 'config/config';
import { TemplateType } from '@buildwise/libs/company-admin';
import {
  CONFIGS,
  getCustomFieldsValidationSchema,
  updateConfig,
  useConfigQuery,
} from '@buildwise/libs/configurations';
import { useDropzone } from 'react-dropzone';
import {
  BUILDWISE_HELP_CENTER_COMPANY_ID,
  getArticleFormDefaultValues,
  getPredefinedFieldValidation,
  useArticleInputFields,
} from '@buildwise/libs/posts';
import { ArticleCreationMethods } from '@buildwise/libs/posts/components/AddPost/ArticleCreationActions';
import { useBuildWiseModules } from '@buildwise/libs/helpers';

export type PostFormData = {
  isDraft?: boolean;
  isWritingWithAI?: boolean;
  articleType?: string;
  relatedRecords?: ArticleRelatedRecordType[];
  [key: string]: any;
};

type UseAddPostFormOptions = {
  postCreationMethod?: ArticleCreationMethods;
  articleType: ArticleSourceType;
  articleFieldsType: ArticleFieldsType;
  articleId?: string;
  articleFormState?: PostFormData;
  createPlaylist?: boolean;
  data?: ArticleType;
  defaultPlaylists?: { label: string; value: string }[];
  defaultProjectId?: string;
  formIds?: string[];
  forward?: boolean;
  selectedFeedFilters?: FeedFilter[];
  isCopyPost?: boolean;
  isEditing?: boolean;
  project?: ProjectRecommendationsType;
  publishArticleToCompanyIds?: string[];
  stakeholderId?: string;
  updateViewCount?: boolean;
  onAddArticleCallback?: (options: { article: ArticleType }) => void;
  onSaveCallback?: (article: ArticleType) => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  updateArticleCallback?: (options: {
    oldArticle: ArticleType;
    updatedArticle: ArticleType;
  }) => void;
  state?: any;
};

type ProjectsSearchResponseType = ProjectsPaginationResponseType;

const createPostFormSchema = ({
  articleFields,
  isCreatingOrEditingPlaylist,
}: {
  articleFields: ConfigEntryType[];
  isCreatingOrEditingPlaylist: boolean;
}) => {
  const customFieldsValidations = getCustomFieldsValidationSchema({
    fields: articleFields,
    doNotAddRequiredValidation: isCreatingOrEditingPlaylist,
    getPredefinedFieldValidation: ({ field }) =>
      getPredefinedFieldValidation({ field, isCreatingOrEditingPlaylist }),
  });

  return Yup.object().shape({
    ...customFieldsValidations,
    // Check if divisions is enabled, then provide respective divisionChips validation schema
    ...(customFieldsValidations?.['divisions'] && {
      divisionChips: Yup.array(Yup.string()),
    }),
    // Check if specSections is enabled, then provide respective specSectionChips validation schema
    ...(customFieldsValidations?.['specSections'] && {
      specSectionChips: Yup.array(Yup.string()),
    }),
    isDraft: Yup.boolean(),
    isWritingWithAI: Yup.boolean(),
  });
};

const useAddPostForm = ({
  postCreationMethod = ArticleCreationMethods.WRITE_MANUALLY,
  articleFormState,
  articleId,
  articleType,
  articleFieldsType,
  createPlaylist = false,
  data: articleData,
  defaultPlaylists = [],
  defaultProjectId = '',
  formIds = [],
  forward,
  selectedFeedFilters,
  isCopyPost,
  isEditing = false,
  project,
  publishArticleToCompanyIds = [],
  stakeholderId,
  updateViewCount = false,
  onAddArticleCallback,
  onSaveCallback,
  setOpen,
  updateArticleCallback,
}: UseAddPostFormOptions) => {
  const { user, companyId, isNovaAdmin, company, isBuildWiseInsightsUser } = useUserData();
  const { updateFeedCache, resetFeedQueries } = useArticlesAndPlaylistsPaginationQuery({
    shouldFetch: false,
    filters: selectedFeedFilters,
  });
  const { updateUserArticlesCache } = useUserArticlesQuery({ shouldFetch: false });
  const { updateBookmarksCache } = useBookmarksQuery({ shouldFetch: false });
  const { data: projectsData } = useSearchItemQuery<ProjectsSearchResponseType>(
    {
      searchType: 'projects',
      operator: 'or',
      filterBy: {
        filters: [
          {
            filterType: 'name',
            filterValue: 'ALL',
          },
        ],
      },
    },
    false,
  );
  const navigate = useNavigate();
  const { pathname, state: routeState, hash } = useLocation();
  const [searchParams] = useSearchParams();
  const [isSavingDraft, setSavingDraft] = useState(false);
  const [isFullScreen, setFullScreen] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [startAnotherPost, setStartAnotherPost] = useState(false);
  const [articleCreationMethod, setArticleCreationMethod] = useState(postCreationMethod);
  const canPublishPost = useMemo(
    () => !((isNovaAdmin || isBuildWiseInsightsUser) && company?.settings?.externalFlag),
    [company?.settings.externalFlag, isNovaAdmin, isBuildWiseInsightsUser],
  );
  const [discardLoading, setDiscardLoading] = useState<boolean>(false);
  const { articleResponse: fetchedArticle } = useArticleDetailsQuery({
    articleId: articleId || articleData?.id || '',
    updateViewCount,
  });
  const data = useMemo(
    () => fetchedArticle?.companyArticle?.article || articleData,
    [articleData, fetchedArticle],
  );
  const { config: hashtagConfig, updateConfigCache } = useConfigQuery({
    companyId,
    configName: CONFIGS.HASHTAGS.configName,
  });
  const isCreatingOrEditingPlaylist = useMemo(
    () => createPlaylist || data?.originIndicator === 'PLAYLIST',
    [createPlaylist, data],
  );
  const { articleFormConfigName, articleFields, isLoadingArticleConfigFields } =
    useArticleInputFields({
      articleFieldsType,
      isEditing,
      isHelpCenterCollection:
        articleType === ArticleSourceType.HELP_CENTER && isCreatingOrEditingPlaylist,
    });

  const postForm = useForm<PostFormData>({
    resolver: yupResolver(
      createPostFormSchema({
        articleFields,
        isCreatingOrEditingPlaylist,
      }),
    ),
    defaultValues: getArticleFormDefaultValues({
      article: data,
      defaultPlaylists,
      defaultProjectId,
      articleFields,
      articleType,
    }),
  });
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateType | undefined>(undefined);
  const [openTemplates, setOpenTemplates] = useState(false);
  const {
    control,
    formState: { isDirty, dirtyFields, isSubmitting },
    getValues,
    handleSubmit,
    reset,
    setValue,
    resetField,
  } = postForm;
  const isWritingWithAI = useWatch({ control, name: 'isWritingWithAI', defaultValue: false });
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setLoadingFiles(true);
      attachFiles({
        newAttachments: acceptedFiles,
        attachments: getValues('attachments') || [],
        setAttachments: (attachments: AttachmentType[]) => {
          setValue('attachments', attachments, { shouldDirty: true, shouldValidate: true });
          setLoadingFiles(false);
        },
        replace: articleCreationMethod === ArticleCreationMethods.PARSE_FILE,
        acceptedFiles:
          articleCreationMethod === ArticleCreationMethods.PARSE_FILE ? DOCUMENT_TYPES : undefined,
        maxFileSize:
          articleCreationMethod === ArticleCreationMethods.PARSE_FILE ? 4 * 1024 * 1024 : Infinity,
      });
    },
    [articleCreationMethod],
  );
  const { getRootProps, isDragActive } = useDropzone({
    onDrop,
    ...(articleCreationMethod === ArticleCreationMethods.PARSE_FILE && {
      accept: DOCUMENT_MIME_WITH_EXTENSIONS_TYPES,
    }),
    noClick: true,
  });
  const [isPublishingFileAsOnePost, setPublishingFileAsOnePost] = useState(false);

  useEffect(() => {
    const { title } = getValues();
    if (title && isCopyPost) {
      setValue('title', `Copy of - ${title}`, { shouldDirty: true, shouldValidate: true });
    }
  }, [isCopyPost, getValues, setValue]);

  useEffect(() => {
    if (forward) {
      downloadArticleAttachments();
    }
  }, [forward]);

  useEffect(() => {
    if (fetchedArticle?.companyArticle?.article || articleFields) {
      reset(
        getArticleFormDefaultValues({
          article: fetchedArticle?.companyArticle?.article || articleData,
          defaultPlaylists,
          defaultProjectId,
          articleFields,
          articleType,
        }),
        {
          /*
          TODO: Since BE is sending the limited description from GET /articles API, description field value isn't getting updated with full value fetched separately here in this form. It's all due to embedded images. So, hiding this value keepDirtyValues for now. Once we migrate file and embedded image uploads to S3, uncomment this keepDirtyValues.
        */
          // keepDirtyValues: true,
          keepErrors: true,
          keepTouched: true,
        },
      );
    }
  }, [fetchedArticle?.companyArticle?.article, articleFields, articleType]);
  const { singularCollectionsModuleName } = useBuildWiseModules();

  useEffect(() => {
    if (articleFormState) {
      reset(articleFormState, {
        keepDirtyValues: true,
        keepErrors: true,
        keepTouched: true,
      });
    }
  }, [articleFormState, isLoadingArticleConfigFields]);

  const downloadArticleAttachments = useCallback(async () => {
    const downloadAttachmentsPromises: Promise<boolean>[] = [];
    const allFiles: File[] = [];

    if (data?.attachments) {
      setLoadingFiles(true);
      const attachmentsToClone = data?.attachments.filter(
        (attachment) => !attachment.imageHashValue,
      );

      for (const attachment of attachmentsToClone) {
        downloadAttachmentsPromises.push(
          new Promise(async (resolve, reject) => {
            try {
              const { data: fileBlob } = await axios(attachment.articleOpenUrl || '', {
                responseType: 'blob',
              });

              let fileType = '';

              if (IMAGE_TYPES.includes(attachment.fileType?.toLowerCase())) {
                fileType = `image/${attachment.fileType?.toLowerCase()}`;
              }

              if (VIDEO_TYPES.includes(attachment.fileType?.toLowerCase())) {
                fileType = `video/${attachment.fileType?.toLowerCase()}`;
              }

              if (AUDIO_TYPES.includes(attachment.fileType?.toLowerCase())) {
                fileType = `audio/${attachment.fileType?.toLowerCase()}`;
              }

              if (DOCUMENT_TYPES.includes(attachment.fileType?.toLowerCase())) {
                if (attachment.fileType?.toLowerCase() === 'txt') {
                  fileType = `text/${attachment.fileType?.toLowerCase()}`;
                } else {
                  fileType = `application/${attachment.fileType?.toLowerCase()}`;
                }
              }

              const newFile = new File([fileBlob], getExactFileName(attachment), {
                lastModified: new Date().getTime(),
                type: fileType,
              });
              allFiles.push(newFile);
              resolve(true);
            } catch (error) {
              reject(error);
            }
          }),
        );
      }

      try {
        await Promise.all(downloadAttachmentsPromises);
        attachFiles({
          newAttachments: allFiles,
          attachments: getValues('attachments') || [],
          setAttachments: (attachments: AttachmentType[]) => {
            setValue('attachments', attachments, { shouldDirty: true, shouldValidate: true });
          },
        });
      } catch (error) {
        console.error('DOWNLOAD_ARTICLE_ATTACHMENTS_ERROR', error);
      } finally {
        setLoadingFiles(false);
      }
    }
  }, [data]);

  const closeModal = useCallback(
    async (article?: ArticleType) => {
      try {
        if (
          !!article &&
          ['SUMMARIZE_FROM_FILE', 'CONVERT_FROM_TEXT'].includes(article?.originIndicator) &&
          article?.isDraft
        ) {
          setDiscardLoading(true);
          const payload: any = {
            ...article,
            isDraft: false,
            status: 'DISCARDED',
          };
          const articleResponse = await KnowledgeAPI.editArticle(companyId, article.id, payload);
          if (articleResponse?.data?.id) {
            toast.success('Post discarded successfully!');
          }
        }
      } catch (error) {
        toast.error(catchErrorMessage(error));
      } finally {
        setDiscardLoading(false);
        setSelectedTemplate(undefined);
        setOpen(false);
        /* 
          TODO: Not sure why we are navigating to current path without hash, search params and state. 
          Without them, I see issues in searched article details page. So, I added them to fix the issue. 
          Check if this is valid use to navigate else remove it.
        */
        navigate(`${pathname}${hash}${searchParams ? `?${searchParams.toString()}` : ''}`, {
          replace: true,
          state: routeState,
        });
      }
    },
    [companyId, pathname, hash, searchParams, routeState],
  );

  const addSuggestionComment = async (comment: string) => {
    try {
      if (user && data?.id && comment.trim()) {
        const { data: commentResData } = await KnowledgeAPI.addComment(companyId, data.id, {
          commentAuthor: user?.username,
          commentText: comment?.trim(),
          mentions: [],
        });
        updateArticleCommentsCache(companyId, data.id, commentResData?.comment, 'add');
      }
    } catch (error) {
      console.log('ADD_SUGGESTION_COMMENT_ERROR', error);
    }
  };

  const updateHashtagConfig = async ({
    config,
    newValue,
  }: {
    config: ConfigType;
    newValue: string[];
  }) => {
    try {
      if (!!config && newValue?.length) {
        const response = await updateConfig({ newValue, config, companyId });
        const data = response?.data;
        if (data) {
          updateConfigCache({
            companyId,
            configName: CONFIGS.HASHTAGS.configName,
            updatedConfig: data,
          });
        }
      }
    } catch (error) {
      console.log('UPDATE_HASHTAG_CONFIG_VALUES_ERROR', error);
    }
  };

  const handleSave = useCallback(
    async (values: PostFormData) => {
      if (values.isDraft) {
        setSavingDraft(true);
      }

      try {
        const payload: Record<string, any> = {
          isDraft: values.isDraft,
          status: values.isDraft ? data?.status : 'PUBLISHED',
          // We are hardcoding articleType for buildwise helpcenter workspace
          articleType:
            companyId === BUILDWISE_HELP_CENTER_COMPANY_ID
              ? ArticleSourceType.HELP_CENTER
              : values?.articleType || articleType,
          relatedRecords: values?.relatedRecords || [],
          playListArticles: values?.playListArticles || [],
        };

        if (formIds.length > 0) {
          payload['formIds'] = formIds;
        }

        // Format config field values
        const customFieldsValues: Record<string, CustomFieldValueType> = {};
        const articleKeys = Object.keys(data || {});
        articleFields.forEach((field) => {
          if (
            Array.isArray(values[field.entryCode])
              ? values[field.entryCode].length > 0
              : values[field.entryCode]
          ) {
            if (
              (articleKeys.includes(field.entryCode) ||
                field.entryType?.toUpperCase() !== ConfigFieldEntryType.CUSTOM) &&
              field.entryCode !== 'attachments'
            ) {
              payload[field.entryCode] = values[field.entryCode];
            } else {
              if (field.entryType?.toUpperCase() === ConfigFieldEntryType.CUSTOM) {
                customFieldsValues[field.entryCode] = {
                  fieldName: field.entryCode,
                  fieldValue: values[field.entryCode],
                  type: field.referenceObject?.type || '',
                  configRefName: field.referenceObject?.configRefName || '',
                  label: field.referenceObject?.label || field.entryDescription,
                };
              }
            }
          }
        });
        payload['customFields'] = customFieldsValues;

        // Extract mentions from description
        const parser = new DOMParser();
        const doc = parser.parseFromString(payload?.description, 'text/html');
        const mentionElements = doc.querySelectorAll('.mention');
        const mentions = mentionElements?.length
          ? Array.from(mentionElements).map((el) => {
              const name = el?.textContent || '';
              return {
                userName: el?.getAttribute('data-mention-id'),
                displayName: name,
              };
            })
          : [];
        payload['mentions'] = mentions;

        // Extract hashtags from description
        let descHashtags: string[] = (payload?.description?.match(/#\w+/g) || []).map((tag) =>
          tag.slice(1),
        );

        // Add new hashtags to company config if any
        updateHashtagConfig({ config: hashtagConfig as ConfigType, newValue: descHashtags });

        payload['labels'] = descHashtags;

        // Format predefined fields
        if (values.playlists || values.playListArticleIds) {
          payload.playListArticleIds = _.uniq([
            ...(values?.playListArticleIds || []),
            ...(values?.playlists?.map?.((playlist) => playlist.value) || []),
          ]);
          delete payload?.customFields?.playListArticleIds;
        }

        if (values.divisions || values.divisionChips) {
          payload.divisions = _.uniq([
            ...(values.divisionChips || []),
            ...(values.divisions || []),
          ]);
          delete payload?.customFields?.divisions;
        }
        if (values.specSections || values.specSectionChips) {
          payload.specSections = _.uniq([
            ...(values.specSectionChips || []),
            ...(values.specSections || []),
          ]);
          delete payload?.customFields?.specSections;
        }

        if (values.projectPhases) {
          payload.projectPhases = Array.isArray(values.projectPhases)
            ? values.projectPhases
            : [values.projectPhases];
          delete payload?.customFields?.projectPhases;
        }
        if (values.topics) {
          payload.topics = Array.isArray(values.topics) ? values.topics : [values.topics];
          delete payload?.customFields?.topics;
        }
        if (values.marketSector) {
          payload.marketSector = Array.isArray(values.marketSector)
            ? values.marketSector
            : [values.marketSector];
          delete payload?.customFields?.marketSector;
        }
        if (values.region) {
          payload.region = Array.isArray(values.region) ? values.region : [values.region];
          delete payload?.customFields?.region;
        }

        // Retro lesson form specific fields
        if (values.lessonTypes) {
          payload.lessonTypes = Array.isArray(values.lessonTypes)
            ? values.lessonTypes
            : [values.lessonTypes];
          delete payload?.customFields?.lessonTypes;
        }
        if (values.projectMileStones) {
          payload.projectMileStones = Array.isArray(values.projectMileStones)
            ? values.projectMileStones
            : [values.projectMileStones];
          delete payload?.customFields?.projectMileStones;
        }
        payload.projectSpecific = !!values.projectSpecific;

        // Write with AI Origin indicator
        delete payload.isWritingWithAI;
        if (values.isWritingWithAI) {
          payload.originIndicator = 'CONVERT_FROM_TEXT';
        }

        // Parse a File Origin indicator
        if (isPublishingFileAsOnePost) {
          payload.originIndicator = 'SUMMARIZE_FROM_FILE';
        }

        let coverPhoto: AttachmentType | undefined = undefined;
        let remainingAttachments: AttachmentType[] = [...(values.attachments || [])];

        if (!isEditing) {
          const coverPhotoIndex = remainingAttachments.findIndex(
            (attachment) => getFileDetails({ file: attachment }).isMediaFile,
          );

          if (coverPhotoIndex > -1) {
            coverPhoto = remainingAttachments[coverPhotoIndex];
            remainingAttachments.splice(coverPhotoIndex, 1);
          }
        }
        let articleResponse;
        if (isEditing && data?.id) {
          // payload.playListArticleIds =
          //   values?.playListArticleIds?.length > 0 ? values.playListArticleIds : [];

          // Suggestion fields
          delete payload.comment;
          addSuggestionComment(values.comment || '');

          const updatedPayload = {
            ...data,
            ...payload,
          };
          delete updatedPayload?.project;
          delete updatedPayload?.primaryCompanyName;

          articleResponse = await KnowledgeAPI.editArticle(companyId, data.id, updatedPayload);
        } else {
          delete payload.attachments;
          if (createPlaylist) {
            payload.originIndicator = 'PLAYLIST';
          }

          if (isCopyPost) {
            payload.attachments = remainingAttachments.filter(
              (file) => file.fileUploaded && !file.isRemoved && !file.isUpdated,
            );
          }

          if (coverPhoto) {
            payload.attachment = coverPhoto;
          }

          // Suggestion fields
          if (articleType === ArticleSourceType.SUGGESTION) {
            payload['taskStatus'] = 'NEW';
          }

          articleResponse = await KnowledgeAPI.addArticle(companyId, payload);
        }

        let removableFiles =
          remainingAttachments
            ?.filter((file) => file.fileUploaded && file.isRemoved)
            .map((file) => file.id) || [];
        let newFiles = remainingAttachments?.filter((file) => !file.fileUploaded) || [];
        let updatedFiles =
          remainingAttachments?.filter((file) => file.fileUploaded && file.isUpdated) || [];

        if (isCopyPost) {
          removableFiles = [];
          updatedFiles = [];
          newFiles =
            remainingAttachments?.filter((file) => !file.fileUploaded || file.isUpdated) || [];
        }

        if (newFiles.length || updatedFiles.length || removableFiles.length) {
          const updateArticleMediaPromises: Promise<AxiosResponse<any, Error> | any>[] = [];

          if (newFiles.length) {
            const formData = new FormData();

            newFiles.forEach((attachment) => {
              formData.append('file', attachment.file);
              if (attachment.thumbnail) {
                formData.append('thumbnail', attachment.thumbnail);
              }
            });

            updateArticleMediaPromises.push(
              KnowledgeAPI.addArticleMedia(companyId, articleResponse?.data?.id, formData),
            );
          }

          if (updatedFiles.length) {
            const formData = new FormData();

            updatedFiles.forEach((attachment) => {
              formData.append('file', attachment.file);
              if (attachment.thumbnail) {
                formData.append('thumbnail', attachment.thumbnail);
              }
            });

            updateArticleMediaPromises.push(
              KnowledgeAPI.updateArticleMedia(companyId, articleResponse?.data?.id, formData),
            );
          }

          if (removableFiles.length) {
            const removeAttachmentsPromises = removableFiles.map((id) =>
              KnowledgeAPI.removeAttachment(companyId, articleResponse?.data?.id, id),
            );

            updateArticleMediaPromises.push(Promise.all(removeAttachmentsPromises));
          }

          const [res1, res2] = await Promise.all(updateArticleMediaPromises);

          let updatedArticle: ArticleType | undefined = articleResponse?.data;

          if ((newFiles.length || updatedFiles.length) && res1) {
            updatedArticle = res1.data;
          }

          if (newFiles.length && updatedFiles.length && res2) {
            updatedArticle = res2.data;
          }

          if (!_.isEmpty(updatedArticle)) {
            if (removableFiles.length) {
              updatedArticle = {
                ...updatedArticle,
                attachments:
                  updatedArticle?.attachments?.filter(
                    (attachment) => !removableFiles.includes(attachment.id),
                  ) || [],
              };
            }

            if (data && !updatedArticle?.isDraft) {
              updateArticleCallback?.({
                oldArticle: data,
                updatedArticle: {
                  ...updatedArticle,
                  project:
                    (updatedArticle.projectId || data?.projectId) && projectsData?.projects
                      ? projectsData?.projects?.find(
                          (project) => project.id === updatedArticle?.projectId,
                        )
                      : undefined,
                },
              });
            }

            if (onSaveCallback) {
              onSaveCallback(updatedArticle);
            } else {
              if (updatedArticle && !updatedArticle.isDraft) {
                updateFeedCache({
                  action: 'update',
                  article: updatedArticle,
                });
                if (updatedArticle.isArticleBookMarked) {
                  updateBookmarksCache({ action: 'update', article: updatedArticle });
                }
                if (project?.id) {
                  updateProjectRecommendationsQueryCache({
                    companyId,
                    projectId: project?.id,
                    articles: [updatedArticle],
                    action: 'update-article',
                  });
                }
                // TODO: update cache of GET /playlists API
              }

              if (updatedArticle && updatedArticle.isDraft) {
                updateDraftsCache(companyId, updatedArticle, 'update');
              }

              if (isEditing && updatedArticle) {
                updateArticleDetailsCache(companyId, {
                  ...updatedArticle,
                  project:
                    (updatedArticle.projectId || data?.projectId) && projectsData?.projects
                      ? projectsData?.projects?.find(
                          (project) => project.id === updatedArticle?.projectId,
                        )
                      : undefined,
                });
              }
            }
          }
        } else {
          if (data && !articleResponse?.data?.isDraft) {
            updateArticleCallback?.({
              oldArticle: (data || fetchedArticle?.companyArticle?.article) as ArticleType,
              updatedArticle: {
                ...articleResponse?.data,
                project:
                  (articleResponse?.data.projectId || data?.projectId) && projectsData?.projects
                    ? projectsData?.projects?.find(
                        (project) => project.id === articleResponse?.data?.projectId,
                      )
                    : undefined,
              },
            });
          }

          onAddArticleCallback?.({
            article: articleResponse?.data,
          });
          if (onSaveCallback && !articleResponse?.data?.isDraft) {
            onSaveCallback({
              ...articleResponse?.data,
              playListArticleIds: payload?.playListArticleIds || [],
            });
          } else {
            if (!articleResponse?.data?.isDraft) {
              updateFeedCache({
                action: 'update',
                article: articleResponse.data,
              });
              updateUserArticlesCache({
                action: 'update',
                article: articleResponse?.data,
                _username: articleResponse?.data?.createdBy,
              });
              if (articleResponse?.data?.isArticleBookMarked) {
                updateBookmarksCache({ action: 'update', article: articleResponse?.data });
              }
              if (project?.id) {
                updateProjectRecommendationsQueryCache({
                  companyId,
                  projectId: project?.id,
                  articles: [articleResponse.data],
                  action: 'update-article',
                });
              }
            }

            if (isEditing) {
              updateArticleDetailsCache(companyId, {
                ...articleResponse.data,
                attachments: data?.attachments?.filter(
                  (file) => !removableFiles?.includes(file.id),
                ),
                project:
                  (articleResponse.data.projectId || data?.projectId) && projectsData?.projects
                    ? projectsData?.projects?.find(
                        (project) => project.id === articleResponse?.data.projectId,
                      )
                    : undefined,
              });
            }
          }
        }

        if (payload.originIndicator === 'PLAYLIST' || data?.originIndicator === 'PLAYLIST') {
          toast.success(
            values.isDraft
              ? `${singularCollectionsModuleName} saved to drafts`
              : isEditing && !data?.isDraft
                ? `${singularCollectionsModuleName} has been updated`
                : `${singularCollectionsModuleName} created`,
          );
        } else {
          if (articleFieldsType === ArticleFieldsType.LESSON) {
            toast.success('Information saved successfully!');
          } else {
            const articleTypeDisplayName =
              articleType === ArticleSourceType.QUESTION
                ? 'Question'
                : articleType === ArticleSourceType.SUGGESTION
                  ? 'Suggestion'
                  : articleType === ArticleSourceType.HELP_CENTER
                    ? 'Article'
                    : 'Post';
            toast.success(
              values.isDraft
                ? 'Draft saved successfully'
                : isEditing && !data?.isDraft
                  ? `${articleTypeDisplayName} updated successfully`
                  : `${articleTypeDisplayName} submitted successfully`,
            );
          }

          if (!isEditing && !articleResponse?.data?.isDraft) {
            handlePageScrollPosition('posts', 0, 0, 'reset');
            handlePageScrollPosition('collections', 0, 0, 'reset');
            resetFeedQueries();
          }
        }
        if (data?.isDraft && !articleResponse.data.isDraft && !isCopyPost) {
          updateDraftsCache(companyId, articleResponse.data, 'delete');
          navigate(HOME);
        }

        // TODO: Move this cache updation to respective page
        if (
          stakeholderId &&
          values.partnerIds &&
          data?.partnerIds?.length !== values?.partnerIds?.length
        ) {
          updateStakeholderArticlesCache('delete', stakeholderId, articleResponse.data);
        }
        if (!startAnotherPost) {
          closeModal();
        } else {
          const fieldsToReset = [
            'description',
            'title',
            'attachments',
            'divisions',
            'specSections',
            'specSectionChips',
            'divisionChips',
          ];
          fieldsToReset.forEach((field) => resetField(field));
          setValue('divisionChips', []);
          setValue('specSectionChips', []);
          setStartAnotherPost(false);
        }

        if (values.isDraft) {
          if (!(newFiles.length || updatedFiles.length || removableFiles.length)) {
            updateDraftsCache(companyId, articleResponse.data, 'update');
          }

          if (pathname !== VIEW_DRAFT && !onSaveCallback && !startAnotherPost) {
            navigate(DRAFTS);
          }
        }
        setPublishingFileAsOnePost(false);
      } catch (error) {
        toast.error(catchErrorMessage(error));
      } finally {
        setSavingDraft(false);
      }
    },
    [
      hashtagConfig,
      formIds,
      isEditing,
      data,
      companyId,
      isCopyPost,
      createPlaylist,
      projectsData,
      project,
      stakeholderId,
      pathname,
      articleFields,
      isPublishingFileAsOnePost,
    ],
  );

  const onFileInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const files = [...((event?.target?.files || []) as File[])];
    attachFiles({
      newAttachments: files,
      attachments: getValues('attachments') || [],
      setAttachments: (attachments: AttachmentType[]) => {
        setValue('attachments', attachments, { shouldDirty: true, shouldValidate: true });
      },
      maxFileSize:
        articleCreationMethod === ArticleCreationMethods.PARSE_FILE ? 4 * 1024 : Infinity,
    });
  }, []);

  const handleTemplateSelection = useCallback(
    (template: TemplateType) => {
      if (template.id !== selectedTemplate?.id) {
        setSelectedTemplate(template);
        setValue('title', template.title, {
          shouldDirty: true,
          shouldValidate: true,
          shouldTouch: true,
        });
        setValue('labels', template.labels, {
          shouldDirty: true,
          shouldValidate: true,
          shouldTouch: true,
        });
        setValue('description', template.description, {
          shouldDirty: true,
          shouldValidate: true,
          shouldTouch: true,
        });
      }

      setOpenTemplates(false);
    },
    [selectedTemplate],
  );

  const handleClose = useCallback(() => {
    if (isDirty && Object.keys(dirtyFields).length && !forward) {
      setOpenConfirmation(true);
    } else {
      closeModal();
    }
  }, [isDirty, dirtyFields, forward, closeModal]);

  const onSaveDraft = useCallback(
    handleSubmit(
      (values) => handleSave({ ...values, isDraft: true }),
      () => {
        // Close action modal
        setOpenConfirmation(false);
      },
    ),
    [handleSave],
  );

  const onSubmit = useCallback(
    handleSubmit((values) => handleSave({ ...values, isDraft: false })),
    [handleSave],
  );

  const onForward = useCallback(
    handleSubmit(async (values: PostFormData) => {
      try {
        const payload: Record<string, string | number | string[]> = {
          title: values.title?.trim(),
          labels: values.labels,
          description: values.description,
          publishArticleToCompanyIds,
          forwardedFromArticleId: data?.id || '',
        };

        if (values.divisions || values.divisionChips) {
          payload.divisions = _.uniq([
            ...(values.divisionChips || []),
            ...(values.divisions || []),
          ]);
        }
        if (values.specSections || values.specSectionChips) {
          payload.specSections = _.uniq([
            ...(values.specSectionChips || []),
            ...(values.specSections || []),
          ]);
        }

        const formData = new FormData();
        formData.append('article', JSON.stringify(payload));

        values.attachments?.forEach?.((attachment) => {
          formData.append('file', attachment.file);
          if (attachment.thumbnail) {
            formData.append('thumbnail', attachment.thumbnail);
          }
        });

        await KnowledgeAPI.forwardArticle(companyId, formData);
        toast.success('Post forwarded successfully!');
        closeModal();
      } catch (error) {
        toast.error(catchErrorMessage(error));
      }
    }),
    [data, publishArticleToCompanyIds, companyId],
  );

  const resetFormWithState = useCallback(
    (values?: Record<string, any>) => {
      reset({
        ...getArticleFormDefaultValues({
          article: data,
          defaultPlaylists,
          defaultProjectId,
          articleFields,
          articleType,
        }),
        ...values,
      });
    },
    [data, articleFields, articleType],
  );

  return {
    articleCreationMethod,
    articleFormConfigName,
    article: data,
    articleFields,
    canPublishPost,
    companyId,
    discardLoading,
    isCreatingOrEditingPlaylist,
    isLoadingArticleConfigFields,
    isDragActive,
    isFullScreen,
    isNovaAdmin,
    isPublishingFileAsOnePost,
    isSavingDraft,
    isWritingWithAI,
    loadingFiles,
    openConfirmation,
    openTemplates,
    postForm,
    selectedTemplate,
    startAnotherPost,
    isSubmitting,
    closeModal,
    getRootProps,
    handleClose,
    handleTemplateSelection,
    onDrop,
    onFileInputChange,
    onForward,
    onSaveDraft,
    onSubmit,
    resetFormWithState,
    setArticleCreationMethod,
    setFullScreen,
    setOpenConfirmation,
    setOpenTemplates,
    setPublishingFileAsOnePost,
    setStartAnotherPost,
  };
};

export default useAddPostForm;
